/**
 * RegistrationForm - New user registration form
 */
import React, { useState, useEffect } from 'react';
import { DatePicker, Select, Checkbox, message } from 'antd';
import dayjs from 'dayjs';
import { useAuthContext } from '../../context/AuthContext';
import { useAuth } from '../../hooks/useAuth';
import { useAuthForm } from '../../hooks/useAuthForm';
import { SALUTATION_OPTIONS, INPUT_TYPES } from '../../types';
import { getConsentVersions } from '../../services/authApi';
import { AuthHeader, AuthInput, AuthButton } from '../ui';

function RegistrationForm() {
  const { identifier, identifierType, loading, goBack } = useAuthContext();
  const { register } = useAuth();
  const { 
    registrationData, 
    error, 
    handleRegistrationChange,
    validateRegistrationForm,
  } = useAuthForm();
  
  const [consentInfo, setConsentInfo] = useState(null);
  const [tcAccepted, setTcAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  
  // Pre-fill mobile/email based on identifier
  useEffect(() => {
    if (identifierType === INPUT_TYPES.MOBILE) {
      handleRegistrationChange('mobile', identifier);
    } else {
      handleRegistrationChange('email', identifier);
    }
  }, [identifier, identifierType, handleRegistrationChange]);
  
  // Fetch consent versions
  useEffect(() => {
    const fetchConsent = async () => {
      try {
        const info = await getConsentVersions();
        setConsentInfo(info);
      } catch (err) {
        console.error('Failed to fetch consent:', err);
      }
    };
    fetchConsent();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateRegistrationForm();
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      message.error(firstError);
      return;
    }
    
    if (!tcAccepted || !privacyAccepted) {
      message.error('Please accept Terms & Conditions and Privacy Policy');
      return;
    }
    
    const formData = {
      ...registrationData,
      dob: registrationData.dob ? dayjs(registrationData.dob).format('DD-MM-YYYY') : '',
      gender: registrationData.gender || '',
    };
    
    await register(formData, consentInfo);
  };
  
  // Disable dates after today
  const disabledDate = (current) => {
    return current && current > dayjs().endOf('day');
  };
  
  return (
    <div className="relative p-6 md:p-8 bg-white">
      <AuthHeader
        title="Create Account"
        subtitle="Fill in your details to get started"
        showBack
        onBack={goBack}
      />
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Salutation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Salutation <span className="text-red-500">*</span>
          </label>
          <Select
            value={registrationData.salutation || undefined}
            onChange={(value) => handleRegistrationChange('salutation', value)}
            placeholder="Select"
            className="w-full"
            style={{ height: 48 }}
            options={SALUTATION_OPTIONS}
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            popupClassName="z-[9999]"
          />
        </div>
        
        {/* Name fields in a row */}
        <div className="grid grid-cols-2 gap-3">
          <AuthInput
          label={<>First Name <span className="text-red-500">*</span></>}
            
            type="text"
            value={registrationData.firstName}
            onChange={(val) => handleRegistrationChange('firstName', val)}
            placeholder="First name"
            disabled={loading}
          />
          
          <AuthInput
            label={<>Last Name <span className="text-red-500">*</span></>}
            type="text"
            value={registrationData.lastName}
            onChange={(val) => handleRegistrationChange('lastName', val)}
            placeholder="Last name"
            disabled={loading}
          />
        </div>
        
        {/* Mobile */}
        <AuthInput
          label={<>Mobile Number <span className="text-red-500">*</span></>}
          type="tel"
          value={registrationData.mobile}
          onChange={(val) => handleRegistrationChange('mobile', val.replace(/\D/g, '').slice(0, 10))}
          placeholder="10-digit mobile number"
          disabled={loading || identifierType === INPUT_TYPES.MOBILE}
          maxLength={10}
        />
        
        {/* Date of Birth */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <DatePicker
            value={registrationData.dob ? (dayjs.isDayjs(registrationData.dob) ? registrationData.dob : dayjs(registrationData.dob)) : null}
            onChange={(date) => handleRegistrationChange('dob', date)}
            format="DD-MM-YYYY"
            placeholder="Select date of birth"
            disabledDate={disabledDate}
            className="w-full rounded-xl"
            style={{ height: 48 }}
            disabled={loading}
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            popupClassName="z-[9999]"
          />
        </div>
        {/* Gender */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1.5">
    Gender <span className="text-red-500">*</span>
  </label>
  <Select
    value={registrationData.gender || undefined}
    onChange={(value) => handleRegistrationChange('gender', value)}
    placeholder="Select gender"
    className="w-full"
    style={{ height: 48 }}
    disabled={loading}
    getPopupContainer={(triggerNode) => triggerNode.parentElement}
    popupClassName="z-[9999]"
  >
    <Select.Option value="M">Male</Select.Option>
    <Select.Option value="F">Female</Select.Option>
    <Select.Option value="O">Other</Select.Option>
  </Select>
</div>
        {/* Consent checkboxes */}
        <div className="space-y-3 pt-2">
  <div className="flex items-start gap-2">
    <Checkbox
      checked={tcAccepted}
      onChange={(e) => setTcAccepted(e.target.checked)}
      className="mt-0.5"
    />
    <span className="text-sm text-gray-700">
      I agree to the{' '}
      <a
        href={`${process.env.REACT_APP_IMG_BASEURL}${consentInfo?.tcPdf || ''}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#004171] hover:underline"
      >
        Terms & Conditions
      </a>
    </span>
  </div>

  <div className="flex items-start gap-2">
    <Checkbox
      checked={privacyAccepted}
      onChange={(e) => setPrivacyAccepted(e.target.checked)}
      className="mt-0.5"
    />
    <span className="text-sm text-gray-700">
      I agree to the{' '}
      <a
        href={`${process.env.REACT_APP_IMG_BASEURL}${consentInfo?.privacyPdf || ''}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#004171] hover:underline"
      >
        Privacy Policy
      </a>
    </span>
  </div>
</div>
        
        {error && (
          <p className="text-sm text-red-500 text-center">{error}</p>
        )}
        
        <AuthButton
          type="submit"
          loading={loading}
          disabled={!tcAccepted || !privacyAccepted}
          variant="success"
        >
          Create Account
        </AuthButton>
      </form>
    </div>
  );
}

export default RegistrationForm;
